import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';

const AUTO_DISMISS_MS = 7000;

const NotificationToast = ({ toast, onDismiss, onClick }) => {
    useEffect(() => {
        const timer = setTimeout(onDismiss, AUTO_DISMISS_MS);
        return () => clearTimeout(timer);
    }, [onDismiss]);

    return (
        <div
            onClick={onClick}
            className="pointer-events-auto glass-card border border-purple-500/30 rounded-xl shadow-2xl p-4 cursor-pointer hover:border-purple-500/50 transition-colors animate-fade-in"
        >
            <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-600/20 rounded-lg flex-shrink-0">
                    <Bell className="w-4 h-4 text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{toast.title}</p>
                    {toast.message && (
                        <p className="text-xs text-gray-400 line-clamp-2 mt-0.5">{toast.message}</p>
                    )}
                </div>
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                    aria-label="Dismiss notification"
                    className="p-1 text-gray-500 hover:text-white rounded transition-colors flex-shrink-0"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

// Bottom-right popup stack for notifications that arrive while the user is
// actively on the site — separate from the bell dropdown, which only shows
// on demand. Mounted once, app-wide, alongside <Navbar />.
const NotificationToastContainer = () => {
    const { toasts, dismissToast, markAsRead } = useNotifications();
    const navigate = useNavigate();

    if (toasts.length === 0) return null;

    return (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col-reverse gap-3 w-[calc(100%-2rem)] max-w-sm pointer-events-none">
            {toasts.map((toast) => (
                <NotificationToast
                    key={toast.id}
                    toast={toast}
                    onDismiss={() => dismissToast(toast.id)}
                    onClick={() => {
                        markAsRead(toast.id);
                        dismissToast(toast.id);
                        if (toast.link) navigate(toast.link);
                    }}
                />
            ))}
        </div>
    );
};

export default NotificationToastContainer;
